import { useState, useEffect } from "react";
import FormInput from "./FormInput";
import QualificationDropdown from "./QualificationDropdown";
import SubjectDropdown from "./SubjectDropdown";
import {
  getFirestore,
  collection,
  addDoc,
  serverTimestamp,
} from "firebase/firestore";
import app from "../../firebase/firebase";

const db = getFirestore(app);

export default function MockSSCEExaminerForm() {
  const [step, setStep] = useState(1);
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false); // 🔵 NEW

  const [school, setSchool] = useState(
    localStorage.getItem("mockSSCESchool") || ""
  );
  const [principal, setPrincipal] = useState(
    localStorage.getItem("mockSSCEPrincipal") || ""
  );

  const [nominees, setNominees] = useState(
    JSON.parse(localStorage.getItem("mockSSCENominees")) || [
      { name: "", qualification: "", subject: "", experience: "", phone: "" },
      { name: "", qualification: "", subject: "", experience: "", phone: "" },
    ]
  );

  const [errors, setErrors] = useState({});

  // Auto-save to localStorage
  useEffect(() => {
    localStorage.setItem("mockSSCESchool", school);
    localStorage.setItem("mockSSCEPrincipal", principal);
    localStorage.setItem("mockSSCENominees", JSON.stringify(nominees));
  }, [school, principal, nominees]);

  const handleNomineeChange = (i, field, value) => {
    const updated = [...nominees];
    updated[i][field] = value;
    setNominees(updated);
  };

  const validateStep = () => {
    let currentErrors = {};

    if (step === 1) {
      if (!school.trim()) currentErrors.school = "School name is required";
      if (!principal.trim())
        currentErrors.principal = "Principal's name is required";
    } else if (step === 2 || step === 3) {
      const i = step - 2;
      const n = nominees[i];

      if (!n.name.trim()) currentErrors[`name${i + 1}`] = "Name is required";
      if (!n.qualification.trim())
        currentErrors[`qual${i + 1}`] = "Qualification required";
      if (!n.subject.trim())
        currentErrors[`subj${i + 1}`] = "Subject required";
      if (!n.experience || n.experience < 0)
        currentErrors[`exp${i + 1}`] = "Experience must be 0 or more";
      if (!n.phone.trim()) currentErrors[`phone${i + 1}`] = "Phone required";
    }

    setErrors(currentErrors);
    return Object.keys(currentErrors).length === 0;
  };

  const nextStep = () => validateStep() && setStep(step + 1);
  const prevStep = () => setStep(step - 1);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateStep()) return;

    setLoading(true); // 🔵 ENABLE LOADING

    try {
      const collectionRef = collection(db, "mockSSCEExaminers");
      await addDoc(collectionRef, {
        school,
        principal,
        nominees,
        submittedAt: serverTimestamp(),
      });

      localStorage.removeItem("mockSSCESchool");
      localStorage.removeItem("mockSSCEPrincipal");
      localStorage.removeItem("mockSSCENominees");

      setSubmitted(true);
    } catch (error) {
      console.error("Error submitting MOCK SSCE Examiner Form:", error);
      alert("Failed to submit form. Please try again.");
    }

    setLoading(false); // 🔵 STOP LOADING
  };

  if (submitted)
    return (
      <div className="max-w-3xl mx-auto p-8 bg-white shadow-xl rounded-2xl text-center">
        <h2 className="text-3xl font-bold text-green-700 mb-4">
          Form Submitted Successfully!
        </h2>
        <p className="text-gray-600 mb-6">
          Thank you for submitting your MOCK SSCE Examiner Nomination.
        </p>
        <button
          onClick={() => window.location.reload()}
          className="bg-green-600 text-white py-2 px-6 rounded-md hover:bg-green-700 transition"
        >
          Submit Another Form
        </button>
      </div>
    );

  return (
    <div className="max-w-3xl mx-auto p-8 bg-white shadow-xl rounded-2xl">
      {/* 🔙 BACK BUTTON */}
      <button
        onClick={() => window.history.back()}
        className="mb-6 text-sm text-blue-700 hover:text-blue-900 transition flex items-center"
      >
        ← Back
      </button>

      <h2 className="text-3xl font-bold mb-2 text-center text-blue-800">
        MOCK SSCE – Examiner Nomination
      </h2>
      <p className="text-center text-gray-600 mb-8">
        Fill in details step by step. Your progress will be saved automatically.
      </p>

      {/* Progress bar */}
      <div className="flex mb-8">
        {[1, 2, 3, 4].map((s) => (
          <div
            key={s}
            className={`flex-1 h-2 mx-1 rounded-full ${
              step >= s ? "bg-blue-600" : "bg-gray-300"
            }`}
          />
        ))}
      </div>

      <form onSubmit={handleSubmit}>
        {/* STEP 1 */}
        {step === 1 && (
          <div className="space-y-4">
            <FormInput
              label="Name of School"
              value={school}
              onChange={(e) => setSchool(e.target.value)}
              required
              error={errors.school}
            />

            <FormInput
              label="Principal's Name"
              value={principal}
              onChange={(e) => setPrincipal(e.target.value)}
              required
              error={errors.principal}
            />
          </div>
        )}

        {/* STEP 2 & 3 */}
        {(step === 2 || step === 3) &&
          (() => {
            const i = step - 2;

            return (
              <div className="space-y-4 border rounded-xl p-6 shadow-sm bg-gray-50">
                <h3 className="text-xl font-semibold mb-4 text-blue-700">
                  Nominee {i + 1}
                </h3>

                <FormInput
                  label="Full Name"
                  value={nominees[i].name}
                  onChange={(e) =>
                    handleNomineeChange(i, "name", e.target.value)
                  }
                  required
                  error={errors[`name${i + 1}`]}
                />

                <QualificationDropdown
                  value={nominees[i].qualification}
                  onChange={(val) =>
                    handleNomineeChange(i, "qualification", val)
                  }
                  required
                  error={errors[`qual${i + 1}`]}
                />

                <SubjectDropdown
                  value={nominees[i].subject}
                  onChange={(val) => handleNomineeChange(i, "subject", val)}
                  required
                  error={errors[`subj${i + 1}`]}
                />

                <FormInput
                  type="number"
                  label="Marking Experience (years)"
                  value={nominees[i].experience}
                  onChange={(e) =>
                    handleNomineeChange(i, "experience", e.target.value)
                  }
                  required
                  error={errors[`exp${i + 1}`]}
                />

                <FormInput
                  type="tel"
                  label="Phone Number"
                  value={nominees[i].phone}
                  onChange={(e) =>
                    handleNomineeChange(i, "phone", e.target.value)
                  }
                  required
                  error={errors[`phone${i + 1}`]}
                />
              </div>
            );
          })()}

        {/* STEP 4 - REVIEW */}
        {step === 4 && (
          <div className="space-y-4">
            <h3 className="text-xl font-semibold mb-4 text-blue-700">
              Review & Submit
            </h3>

            <p>
              <strong>School:</strong> {school}
            </p>

            <p>
              <strong>Principal's Name:</strong> {principal}
            </p>

            {nominees.map((n, i) => (
              <div key={i} className="border p-4 rounded-md bg-gray-50">
                <h4 className="font-semibold mb-2">Nominee {i + 1}</h4>
                <p>Name: {n.name}</p>
                <p>Qualification: {n.qualification}</p>
                <p>Subject: {n.subject}</p>
                <p>Experience: {n.experience} years</p>
                <p>Phone: {n.phone}</p>
              </div>
            ))}
          </div>
        )}

        {/* BUTTONS */}
        <div className="flex justify-between mt-6">
          {step > 1 && (
            <button
              type="button"
              onClick={prevStep}
              disabled={loading}
              className="bg-gray-200 text-gray-700 px-4 py-2 rounded-md hover:bg-gray-300 transition"
            >
              Previous
            </button>
          )}

          {step < 4 && (
            <button
              type="button"
              onClick={nextStep}
              disabled={loading}
              className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 transition"
            >
              Next
            </button>
          )}

          {step === 4 && (
            <button
              type="submit"
              disabled={loading}
              className={`px-6 py-2 rounded-md text-white flex items-center gap-2 ${
                loading
                  ? "bg-green-400 cursor-not-allowed"
                  : "bg-green-700 hover:bg-green-800"
              }`}
            >
              {loading && (
                <span className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></span>
              )}
              {loading ? "Submitting..." : "Submit Form"}
            </button>
          )}
        </div>
      </form>
    </div>
  );
}
